import { MessageCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getWhatsAppUrl as buildWhatsAppUrl, inferWhatsAppContext } from "../lib/whatsapp";

export interface WhatsAppCTAProps {
  serviceName: string;
  serviceType?: string;
  locationName?: string;
  sourcePage?: string;
  sourcePath?: string;
  variant?: "floating" | "inline" | "hero" | "sticky-bottom";
  className?: string;
}

export default function WhatsAppCTA({
  serviceName,
  serviceType,
  locationName,
  sourcePage,
  sourcePath,
  variant = "inline",
  className,
}: WhatsAppCTAProps) {
  const location = useLocation();
  const inferredContext = inferWhatsAppContext(sourcePath ?? location.pathname);

  const href = buildWhatsAppUrl({
    sourcePath: sourcePath ?? inferredContext.sourcePath,
    sourcePage: sourcePage ?? inferredContext.sourcePage,
    serviceType: serviceType ?? inferredContext.serviceType,
    servicePage: serviceName ?? inferredContext.servicePage,
    locationName,
  });

  const baseStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 6,
    border: "1px solid rgba(37, 211, 102, 0.45)",
    background: "rgba(37, 211, 102, 0.12)",
    color: "#DFFFEF",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 500,
    textDecoration: "none",
    transition: "all 0.2s ease",
  };

  const variantStyle: Record<NonNullable<WhatsAppCTAProps["variant"]>, React.CSSProperties> = {
    inline: { padding: "10px 14px", fontSize: 14 },
    hero: { padding: "12px 18px", fontSize: 15 },
    floating: {
      padding: "12px 14px",
      fontSize: 14,
      position: "fixed",
      right: 20,
      bottom: 22,
      zIndex: 1200,
      boxShadow: "0 10px 26px rgba(37,211,102,0.25)",
    },
    "sticky-bottom": {
      padding: "12px 14px",
      fontSize: 14,
      position: "sticky",
      bottom: 16,
      zIndex: 100,
      boxShadow: "0 10px 26px rgba(37,211,102,0.18)",
    },
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={className}
      style={{ ...baseStyle, ...variantStyle[variant] }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.background = "rgba(37, 211, 102, 0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.background = "rgba(37, 211, 102, 0.12)";
      }}
      aria-label="Chat with OWL on WhatsApp"
    >
      <MessageCircle size={16} />
      <span>Chat on WhatsApp</span>
    </a>
  );
}
